import type { ReceivedSnapshot } from '@apm/contracts';
import { alignToBuckets, bucketStarts, type TrendSeries } from '../charts/trend';
import { STATUS, TX_COLOR } from '../theme';

/**
 * 최근 1시간 초당 요청·오류 추이 (22-B회차, 빠진 정보 5).
 *
 * 인스턴스 카드의 "초당 완료"는 직전 1초 값이라 눈을 떼면 사라진다. 1초 스냅샷의
 * `rate.completed`·`rate.errors`를 1분 구간으로 모아 초당 평균을 낸다. 서버는 손대지 않는다 —
 * 스냅샷은 저장하지 않으므로(실시간 경로 설계) 화면이 받은 것을 모으는 수밖에 없다.
 * 관제 서버가 링버퍼에 30분치(`APM_RT_RETENTION_MS`)를 들고 있어 새로고침해도 그만큼은
 * `GET /api/rt/recent`로 되찾는다. 그 앞은 화면을 열어 둔 동안만 쌓인다.
 *
 * 구간 안에서는 인스턴스별로 따로 센다. 서비스 필터와 맞추기 위해서고, 같은 초의 스냅샷을
 * 두 번 받아도(되찾은 것과 실시간이 겹칠 때) 한 번만 세기 위해서다.
 */

export interface RatePoint {
  bucketStart: number;
  /** 구간에서 관측한 초 수. 인스턴스가 둘이면 같은 초를 한 번만 센다 */
  seconds: number;
  completed: number;
  errors: number;
}

interface InstanceSlot {
  service: string;
  completed: number;
  errors: number;
  /** 이 인스턴스에서 본 초(epoch s). 같은 초의 중복 스냅샷을 거른다 */
  seconds: Set<number>;
}

const BUCKET_MS = 60_000;
const KEEP_MS = 60 * 60_000;

export class RateTrend {
  private readonly buckets = new Map<number, Map<string, InstanceSlot>>();
  /** 값이 하나라도 더해질 때마다 오른다. 화면은 이것만 보고 다시 계산한다 */
  version = 0;

  constructor(
    readonly bucketMs: number = BUCKET_MS,
    private readonly keepMs: number = KEEP_MS,
  ) {}

  /** 스냅샷 하나를 더한다. 이미 본 초이거나 창 밖이면 거짓 */
  add(snapshot: ReceivedSnapshot, now: number = Date.now()): boolean {
    if (snapshot.ts < now - this.keepMs - this.bucketMs) return false;
    const start = Math.floor(snapshot.ts / this.bucketMs) * this.bucketMs;
    const second = Math.floor(snapshot.ts / 1_000);
    const key = `${snapshot.service} ${snapshot.instance}`;
    let slots = this.buckets.get(start);
    if (slots === undefined) {
      slots = new Map();
      this.buckets.set(start, slots);
    }
    let slot = slots.get(key);
    if (slot === undefined) {
      slot = { service: snapshot.service, completed: 0, errors: 0, seconds: new Set() };
      slots.set(key, slot);
    }
    if (slot.seconds.has(second)) return false;
    slot.seconds.add(second);
    slot.completed += snapshot.rate.completed;
    slot.errors += snapshot.rate.errors;
    this.version += 1;
    this.prune(now);
    return true;
  }

  /** 창 밖 구간을 버린다 */
  private prune(now: number): void {
    const cutoff = Math.floor((now - this.keepMs) / this.bucketMs) * this.bucketMs;
    for (const start of this.buckets.keys()) {
      if (start < cutoff) this.buckets.delete(start);
    }
  }

  /** 구간별 합. 서비스를 주면 그 서비스의 인스턴스만 */
  points(now: number = Date.now(), service?: string): RatePoint[] {
    const out: RatePoint[] = [];
    const cutoff = now - this.keepMs;
    for (const [start, slots] of this.buckets) {
      if (start + this.bucketMs < cutoff) continue;
      const seconds = new Set<number>();
      let completed = 0;
      let errors = 0;
      for (const slot of slots.values()) {
        if (service !== undefined && service !== '' && slot.service !== service) continue;
        for (const second of slot.seconds) seconds.add(second);
        completed += slot.completed;
        errors += slot.errors;
      }
      if (seconds.size === 0) continue;
      out.push({ bucketStart: start, seconds: seconds.size, completed, errors });
    }
    return out.sort((a, b) => a.bucketStart - b.bucketStart);
  }

  /** 그래프 계열. 초당 평균은 합계를 관측한 초 수로 나눈다 */
  series(now: number = Date.now(), service?: string): { buckets: number[]; series: TrendSeries[] } {
    const points = this.points(now, service);
    const buckets = bucketStarts(now - this.keepMs, now, this.bucketMs);
    return {
      buckets,
      series: [
        {
          label: '초당 요청',
          color: TX_COLOR.http,
          unit: 'rate',
          shape: 'bar',
          values: alignToBuckets(points, buckets, (point) => point.completed / point.seconds),
        },
        {
          label: '초당 오류',
          color: STATUS.error,
          unit: 'rate',
          shape: 'line',
          values: alignToBuckets(points, buckets, (point) => point.errors / point.seconds),
        },
      ],
    };
  }
}
